import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  AfterViewInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { AngleDownComponent } from '../icons/angle-down';
import { LocaleService } from 'src/app/services/locale';

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AngleDownComponent],
  template: `
    <div class="flex flex-col gap-0.5 relative">
      <label *ngIf="label" class="text-[12px] sm:text-[12px] text-[#4a4a4a] font-medium">{{ label }}</label>
      <div class="relative">
        <select
          class="px-2 py-1.5 sm:py-2 pr-6 appearance-none rounded border w-full border-[#D1D5DB] focus:outline-none focus:border-[#043B22] transition-colors text-[12px]"
          [formControl]="control"
          (change)="onChange($event)"
          [required]="required"
          [ngClass]="{
            'border-red-400 focus:border-red-400': control.invalid && (control.touched || control.dirty),
            'text-[#9CA3AF]': !control.value,
            'bg-gray-100 text-[#616161] cursor-not-allowed': control.disabled,
            'bg-white cursor-pointer': !control.disabled
          }">
          <option value="" disabled selected class="text-[#9CA3AF]">{{ placeholder }}</option>
          <option *ngFor="let option of options; trackBy: trackBy || trackById" [value]="option.value" class="text-[#212121] bg-white">
            {{ language === 'italian'? option.labelIt || option.label: option.label}}
          </option>
        </select>
        <div class="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[#616161]">
          <app-angle-down></app-angle-down>
        </div>
      </div>
      <div *ngIf="control.invalid && (control.touched || control.dirty)" class="text-red-500 text-[12px] flex items-center gap-0.5">
        <svg class="w-2.5 h-2.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        <span>
          <span *ngIf="control.errors?.['required']">
            {{ cleanLabel || (language === 'english' ? 'This field' : 'Questo campo') }}
            {{ locale.common.isRequired }}
          </span>
          <span *ngIf="control.errors?.['email']">
            {{ cleanLabel || (language === 'english' ? 'This field' : 'Questo campo') }}
            {{ locale.common.isInvalid }}
          </span>
        </span>
      </div>
    </div>
  `
})
export class DropdownComponent implements AfterViewInit {
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() control: FormControl = new FormControl();
  @Input() options: any[] = [];
  @Input() trackBy?: (index: number, item: any) => any;
  @Input() required: boolean = false;

  @Output() change = new EventEmitter<any>();

  private localeService = inject(LocaleService);
  locale = this.localeService.locale();
  language = this.localeService.getLanguage();

  constructor() {
    effect(() => {
      this.locale = this.localeService.locale();
      this.language = this.localeService.getLanguage();
    });
  }

  ngAfterViewInit(): void {
    // Re-apply value as a microtask so NgSelectOption instances have registered
    if (this.control?.value) {
      Promise.resolve().then(() => {
        this.control.setValue(this.control.value, { emitEvent: false });
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options'] && this.control?.value) {
      const prev = changes['options'].previousValue as { value: any }[] | null | undefined;
      const curr = changes['options'].currentValue as { value: any }[] | null | undefined;
      // Skip re-sync when only the array reference changed but values are identical.
      // Non-memoized option getters in parent components return a new array on every
      // change detection cycle, causing DropdownComponent.ngOnChanges to fire, which
      // schedules a microtask that resolves inside Angular's zone and triggers another
      // CD cycle — an infinite loop. Guard against this here at the source.
      if (!changes['options'].firstChange && this.optionsValueEqual(prev, curr)) {
        return;
      }
      // Re-sync the select after options are updated so the saved value displays correctly
      Promise.resolve().then(() => {
        this.control.setValue(this.control.value, { emitEvent: false });
      });
    }
    if (changes['required']) {
      if (this.required) {
        this.control.addValidators(Validators.required);
      } else {
        this.control.removeValidators(Validators.required);
      }
      this.control.updateValueAndValidity();
    }
  }

  get cleanLabel(): string {
    return (this.label || '').replace(/\s*\(.*?\)\s*/g, '').trim();
  }

  trackById(index: number, item: any) {
    return item.value;
  }

  onChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.change.emit(value);
  }

  private optionsValueEqual(
    a: { value: any }[] | null | undefined,
    b: { value: any }[] | null | undefined,
  ): boolean {
    if (a === b) return true;
    if (!a || !b || a.length !== b.length) return false;
    return a.every((item, i) => item.value === b[i].value);
  }
}
